import axios from 'axios';
import { isLocalHost } from '../utils/helpers';
import { idleSeconds } from '../utils/userActivity';
import { twoFactorPrompt } from '../utils/twoFactorPrompt';
import { passkeyPrompt } from '../utils/passkeyPrompt';

// Use relative URL in production, localhost in development. The ONE derivation
// of the API base — every call goes through this client (so a 401 reaches the
// interceptor below); nothing else may build its own.
export const API_URL = process.env.REACT_APP_API_URL || (isLocalHost() ? 'http://localhost:5002/api' : '/api');

// Separate axios instance for file uploads (no default JSON Content-Type)
const formDataApi = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  timeout: 60000,
});

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

// Every request tells the server how long since the person last touched the
// page, so background polling never counts as activity for the 1-hour idle
// sign-out (see utils/userActivity.js and the server's idle middleware).
//
// User identities (2026-10-30): one person may hold several roles and the
// cookie is shared by every tab. Each request also names the role account THIS
// page was drawn for (X-Acting-User-Id); if another tab switched role the
// server answers 409 ROLE_SWITCHED and handle401 below reloads the page, so a
// stale tab can never write a record under the wrong role.
let actingUserId = null;
export const setActingUserId = (id) => { actingUserId = id || null; };
const attachIdleHeader = (config) => {
  config.headers = config.headers || {};
  config.headers['X-User-Idle-Seconds'] = String(idleSeconds());
  if (actingUserId) config.headers['X-Acting-User-Id'] = String(actingUserId);
  return config;
};
api.interceptors.request.use(attachIdleHeader);
formDataApi.interceptors.request.use(attachIdleHeader);

// 401 → redirect to /login (403 PASSWORD_CHANGE_REQUIRED → /change-password), applied to both instances.
let roleSwitchReloading = false; // reload once, however many calls fail together
const handle401 = async (error) => {
  // Another tab switched role (or signed in as someone else): this page is
  // stale. Reload once; the fresh bundle reads /me and sends the new id.
  if (error.response?.status === 409 && error.response?.data?.code === 'ROLE_SWITCHED') {
    if (!roleSwitchReloading) {
      roleSwitchReloading = true;
      window.location.reload();
    }
    return Promise.reject(error);
  }
  // A two-factor answer (wrong code, restart, locked) is about the CODE, not
  // the session — never treat it as "signed out". The server sends those as
  // 400/429 with a TWO_FACTOR_* code; this guard is belt and braces.
  const tfCode = String(error.response?.data?.code || '');
  if (tfCode.startsWith('TWO_FACTOR_') || tfCode.startsWith('PASSKEY_')) return Promise.reject(error);
  if (error.response?.status === 401 && window.location.pathname !== '/login') {
    // The server says WHY when it ended the session itself (idle sign-out).
    const idle = String(error.response?.headers?.['x-session-ended'] || '') === 'idle';
    const body = idle
      ? 'You were signed out after 1 hour without activity. Please log in again.'
      : 'You were logged out. Please log in again.';
    // Best-effort OS notification so a cleared/expired session tells the user
    // to re-login even if the tab is backgrounded.
    if ('serviceWorker' in navigator && 'Notification' in window && Notification.permission === 'granted') {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) reg.showNotification('Session ended', { body, tag: 'crm-session', renotify: true, data: { url: '/login' } });
      } catch (e) {}
    }
    window.location.href = idle ? '/login?reason=idle' : '/login';
  }
  // Forced password change (operator rule 2026-10-12): the server refuses every
  // call but /me, /me/password and /logout until the new password is saved.
  // App's gate renders the forced page for ANY path once /me reports the flag,
  // so this redirect is only a safety net for a stale tab.
  if (error.response?.status === 403 && error.response?.data?.code === 'PASSWORD_CHANGE_REQUIRED'
      && window.location.pathname !== '/change-password') {
    window.location.href = '/change-password';
  }
  if (process.env.NODE_ENV === 'development') {
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.message,
    });
  }
  return Promise.reject(error);
};
// Step-up (2FA): a protected call may answer 403 TWO_FACTOR_REQUIRED. Ask for a
// code in the global modal, then retry the ORIGINAL request ONCE, invisibly to
// the caller. Cancel (or a second refusal) rejects with the original error, so
// pages keep showing their usual error toast. Never loops: __twoFactorRetried.
const handleTwoFactor = (instance) => async (error) => {
  const cfg = error.config;
  if (error.response?.status === 403 && error.response?.data?.code === 'TWO_FACTOR_REQUIRED' && cfg && !cfg.__twoFactorRetried) {
    try {
      await twoFactorPrompt.request();
    } catch (cancelled) {
      return Promise.reject(error);
    }
    cfg.__twoFactorRetried = true;
    return instance.request(cfg);
  }
  // Step-up (passkey, 2026-10-24): user-management routes answer 403
  // PASSKEY_REQUIRED — the passkey prompt runs the ceremony, then ONE retry.
  if (error.response?.status === 403 && error.response?.data?.code === 'PASSKEY_REQUIRED' && cfg && !cfg.__passkeyRetried) {
    try {
      await passkeyPrompt.request();
    } catch (cancelled) {
      return Promise.reject(error);
    }
    cfg.__passkeyRetried = true;
    return instance.request(cfg);
  }
  return handle401(error);
};
api.interceptors.response.use((r) => r, handleTwoFactor(api));
formDataApi.interceptors.response.use((r) => r, handleTwoFactor(formDataApi));

// Tiny verb helpers. They unwrap `.data` so the exported functions
// below stay one-liners. The default-exported `api` still returns the
// raw axios response for the one external caller (ImageUploadBox) that
// reads `response.data.url` directly.
const get      = (url, opts)        => api.get(url, opts).then((r) => r.data);
const post     = (url, body, opts)  => api.post(url, body, opts).then((r) => r.data);
const put      = (url, body, opts)  => api.put(url, body, opts).then((r) => r.data);
const del      = (url, opts)        => api.delete(url, opts).then((r) => r.data);
const patch    = (url, body, opts)  => api.patch(url, body, opts).then((r) => r.data);
const postForm = (url, formData)    => formDataApi.post(url, formData).then((r) => r.data);

// ============ Auth ============
export const login           = (username, password) => post('/login', { username, password });
export const logout          = ()                   => post('/logout');
export const getCurrentUser  = ()                   => get('/me');
// Self-service account (2026-10-08): own password / username, proven with the current password.
export const changeMyPassword = (currentPassword, newPassword) => put('/me/password', { current_password: currentPassword, new_password: newPassword });
export const changeMyUsername = (username, currentPassword)    => put('/me/username', { username, current_password: currentPassword });
// Two-factor authentication (authenticator app). `code` = the 6-digit app code.
export const login2fa                = (code) => post('/login/2fa', { code });
export const twoFactorSetupStart     = ()     => post('/2fa/setup/start', {});
export const twoFactorSetupConfirm   = (code) => post('/2fa/setup/confirm', { code });
export const twoFactorVerify         = (code) => post('/2fa/verify', { code });
export const resetUserTwoFactor      = (userId) => post(`/users/${userId}/2fa/reset`, {});
// Passkeys (administrators, 2026-10-24). `answer` = the browser's credential
// response (from utils/webauthn.js); the server verifies it.
export const loginPasskeyOptions     = ()               => post('/login/passkey/options', {});
export const loginPasskeyVerify      = (answer)         => post('/login/passkey/verify', { response: answer });
export const getMyPasskeys           = ()               => get('/passkeys');
export const passkeyRegisterOptions  = ()               => post('/passkeys/register/options', {});
export const passkeyRegisterVerify   = (name, answer)   => post('/passkeys/register/verify', { name, response: answer });
export const deleteMyPasskey         = (id)             => del(`/passkeys/${id}`);
export const passkeyStepUpOptions    = ()               => post('/passkeys/stepup/options', {});
export const passkeyStepUpVerify     = (answer)         => post('/passkeys/stepup/verify', { response: answer });
export const resetUserPasskeys       = (userId)         => post(`/users/${userId}/passkeys/reset`, {});
// User identities (2026-10-30): one sign-in, several roles. chooseLoginRole
// finishes a sign-in that answered roleChoiceRequired; switchRole changes the
// active role (the interceptor above shows the passkey / code prompt it may ask for).
export const chooseLoginRole = (userId) => post('/login/choose-role', { user_id: userId });
export const switchRole      = (userId) => post('/me/switch-role', { user_id: userId });
export const getAppVersion   = ()                   => get('/version'); // deploy-version beacon (public)

// ============ Customers ============
// params = { page, per_page, q } → { rows, total, page, per_page, pages }; with
// no params the server still answers the legacy full array (CryptoManagement).
export const getCustomers                 = (params = {})                 => get('/customers', { params });
export const createCustomer               = (customerData)                => post('/customers', customerData);
export const updateCustomer               = (customerId, customerData)    => put(`/customers/${customerId}`, customerData);
export const deleteCustomer               = (customerId)                  => del(`/customers/${customerId}`);
export const assignCustomer               = (customerId, assignedTo)      => put(`/customers/${customerId}/assign`, { assigned_to: assignedTo });
// opts.scope='all' lets a sales user search across ALL customers (used by the
// customer-takeover flow to find a customer they don't own). Omit it in the
// Create Order / Request Payment pickers so sales stay scoped to their own.
export const searchCustomers              = (query, opts = {})             => get(`/customers/search?q=${encodeURIComponent(query)}${opts.scope ? `&scope=${encodeURIComponent(opts.scope)}` : ''}`);
export const createReassignmentRequest    = (customerId, reason)          => post('/customer-reassignments', { customer_id: customerId, reason });
export const getReassignmentRequests      = ()                            => get('/customer-reassignments');
export const updateReassignmentRequest    = (requestId, status, adminNote) => put(`/customer-reassignments/${requestId}`, { status, admin_note: adminNote });

// ---- Customer VIP tier (credit) ----
export const requestVipUpgrade            = (customerId, reason)          => post(`/customers/${customerId}/vip-request`, { reason });
export const getVipRequests               = ()                            => get('/customer-vip-requests');
export const resolveVipRequest            = (requestId, status, adminNote) => put(`/customer-vip-requests/${requestId}`, { status, admin_note: adminNote });
export const setCustomerCreditStatus      = (customerId, creditStatus)     => put(`/customers/${customerId}/credit-status`, { credit_status: creditStatus });
export const getSalesUsers                = ()                            => get('/users/sales');

// Rename a customer. Used by the Edit button on a customer card in Money
// Available — those "accounts" are synthesized from customer_account_balances
// JOIN customers, so they MUST be renamed via this endpoint (customers.id)
// rather than PUT /api/bank-accounts/:id (different row).
export const renameCustomer = (customerId, { customer_name, customer_nickname } = {}) =>
  put(`/customers/${customerId}/rename`, {
    customer_name,
    ...(customer_nickname !== undefined ? { customer_nickname } : {}),
  });

// ============ Users (admin only) ============
export const getUsers           = ()                          => get('/users');
export const createUser         = (userData)                  => post('/users', userData);
// Permanent delete: the row stays as a tombstone renamed "<name> (deleted #id)"
// so every historical record still resolves the name, the original username is
// freed for a brand-new account, and there is NO restore. It 409s with an
// itemised `blockers` payload while the person still holds live work.
export const deleteUser         = (userId)                    => del(`/users/${userId}`);
// restoreUser removed (2026-08-13): deletion is permanent — the tombstone is
// renamed "<name> (deleted #id)" to free the username for a new account.
export const changeUserRole     = (userId, role)              => put(`/users/${userId}/role`, { role });
export const renameUserNickname = (userId, nickname)          => put(`/users/${userId}/nickname`, { nickname });
export const getUserLinks       = (userId)                    => get(`/users/${userId}/deletion-blockers`);
export const changeUserPassword = (userId, newPassword)       => put(`/users/${userId}/password`, { newPassword });

// ---- People (identities, 2026-10-30): one sign-in, one or more role accounts ----
export const getIdentities          = ()                   => get('/identities');
export const addIdentityRole        = (identityId, body)   => post(`/identities/${identityId}/roles`, body); // { role, nickname }
export const deleteIdentity         = (identityId)         => del(`/identities/${identityId}`);
export const mergeIdentity          = (targetId, sourceId) => post(`/identities/${targetId}/merge`, { source_identity_id: sourceId });
export const resetIdentityPassword  = (identityId, pw)     => put(`/identities/${identityId}/password`, { newPassword: pw });
export const resetIdentityTwoFactor = (identityId)         => post(`/identities/${identityId}/2fa/reset`, {});
export const resetIdentityPasskeys  = (identityId)         => post(`/identities/${identityId}/passkeys/reset`, {});
export const resetIdentityTelegram  = (identityId)         => post(`/identities/${identityId}/telegram/reset`, {});

// Telegram link (2026-10-30): every person links one Telegram account.
export const getMyTelegram          = ()                   => get('/me/telegram');
export const newTelegramLinkToken   = ()                   => post('/me/telegram/link-token', {});

// ============ Bank accounts & transactions ============
export const getBankAccounts     = ()                                    => get('/bank-accounts');
export const createBankAccount   = (accountData)                         => post('/bank-accounts', accountData);
export const updateBankAccount   = (accountId, accountData)              => put(`/bank-accounts/${accountId}`, accountData);
export const deleteBankAccount   = (accountId)                           => del(`/bank-accounts/${accountId}`);
// params = { page, per_page } → server-paged { rows, total, page, per_page, pages }
// (utils/paging.js); no params → the legacy full array (pickers, summaries).
export const getTransactions     = (accountId, params)                   => get(`/bank-accounts/${accountId}/transactions`, params ? { params } : undefined);
export const createTransaction   = (accountId, transactionData)          => post(`/bank-accounts/${accountId}/transactions`, transactionData);
export const updateTransaction   = (transactionId, transactionData)      => put(`/transactions/${transactionId}`, transactionData);
export const deleteTransaction   = (transactionId)                       => del(`/transactions/${transactionId}`);
export const createBankSwap      = (swapData)                            => post('/bank-swap', swapData);

// ============ Crypto ============
export const getCryptoAccounts          = ()                                   => get('/crypto-accounts');
export const createCryptoGroup          = (groupData)                          => post('/crypto-groups', groupData);
export const createSubaccount           = (groupId, subaccountData)            => post(`/crypto-groups/${groupId}/subaccounts`, subaccountData);
export const createCustomerSubaccount   = (subaccountData)                     => post('/crypto-customer-subaccounts', subaccountData);
export const deleteCryptoGroup          = (groupId)                            => del(`/crypto-groups/${groupId}`);
export const deleteSubaccount           = (subaccountId)                       => del(`/crypto-subaccounts/${subaccountId}`);
export const archiveSubaccount          = (subaccountId, archived = true)      => put(`/crypto-subaccounts/${subaccountId}/archive`, { archived });
// Frozen coins (2026-10-23): how much of one wallet's coin balance is frozen by the exchange / issuer.
export const setCryptoFrozen            = (subaccountId, cryptoType, frozenAmount) => put('/crypto-balances/frozen', { subaccount_id: subaccountId, crypto_type: cryptoType, frozen_amount: frozenAmount });
export const createCryptoTransaction    = (transactionData)                    => post('/crypto-transactions', transactionData);
export const createCryptoSwap           = (swapData)                           => post('/crypto-swap', swapData);
export const updateCryptoTransaction    = (transactionId, transactionData)     => put(`/crypto-transactions/${transactionId}`, transactionData);
export const deleteCryptoTransaction    = (transactionId)                      => del(`/crypto-transactions/${transactionId}`);
export const deleteBlockchainTransaction = (transactionId)                     => del(`/blockchain-transactions/${transactionId}`);

// params = { page, per_page } → server-paged { rows, total, page, per_page, pages }
// (utils/paging.js); no params → the legacy full array.
export const getCryptoTransactions = (subaccountId, cryptoType, params) =>
  get(cryptoType
    ? `/crypto-transactions/${subaccountId}?crypto_type=${cryptoType}`
    : `/crypto-transactions/${subaccountId}`, params ? { params } : undefined);

// ============ Orders ============
export const getOrders         = (params = {})               => get('/orders', { params });
// Orders that were deleted (cancelled) — read-only archive, same visibility rule as getOrders.
export const getCancelledOrders = (params = {})              => get('/orders/cancelled', { params });
// Currency codes usable as crypto_type / settle_currency on an order (upper-case).
export const getOrderCurrencies = ()                          => get('/orders/currencies');
export const createOrder       = (orderData)                 => post('/orders', orderData);
export const updateOrder       = (orderId, orderData)        => put(`/orders/${orderId}`, orderData);
// Market price (USDT per 1 unit) on an existing order — commission metadata only.
export const setOrderReference = (orderId, referenceUsdtRate) => patch(`/orders/${orderId}/reference`, { reference_usdt_rate: referenceUsdtRate });
export const deleteOrder       = (orderId)                   => del(`/orders/${orderId}`);
export const getUnlinkedTransactions = ()                    => get('/transactions/unlinked');

export const getUnlinkedCryptoTransactions = (cryptoType, transactionType) => {
  const params = {};
  if (cryptoType) params.crypto_type = cryptoType;
  if (transactionType) params.transaction_type = transactionType;
  return get('/crypto-transactions/unlinked', { params });
};

// ============ Wallet addresses ============
export const linkWalletAddress = (addressData) => post('/blockchain/address-labels', addressData);
export const getAddressLabels  = ()            => get('/blockchain/address-labels');
export const getMonitoredAddresses = ()        => get('/blockchain/addresses');
// Monitored-address CRUD + per-address transaction feed (ported off raw fetch
// 2026-08-02 — bare fetch('/api…') bypassed the 401 session-expiry interceptor
// and broke on any origin without an /api proxy; same class as the Expenses
// port). `qs` is the caller-built query suffix so request shapes stay
// byte-identical to the old fetches.
export const getBlockchainAddressTransactions = (addressId, qs = '') => get(`/blockchain/addresses/${addressId}/transactions${qs}`);
export const addMonitoredAddress          = (data)          => post('/blockchain/addresses', data);
export const deleteMonitoredAddress       = (id)            => del(`/blockchain/addresses/${id}`);
export const setMonitoredAddressArchived  = (id, archived)  => put(`/blockchain/addresses/${id}/archive`, { archived });
export const updateMonitoredAddressName   = (id, label)     => put(`/blockchain/addresses/${id}/label`, { label });
// Chain reconciliation (2026-09-24): read-only on-chain vs recorded balance
// check (finance/admin), and an admin-only booking of an adjustment that
// brings the recorded balance in line with the chain (NAV-neutral — see
// POST /api/blockchain/addresses/:id/reconcile on the backend).
export const getChainBalance              = (addressId)     => post(`/blockchain/addresses/${addressId}/chain-balance`, {});
export const reconcileChainBalance        = (addressId, token, expectedOnChain) =>
  post(`/blockchain/addresses/${addressId}/reconcile`, { token, expected_on_chain: expectedOnChain });
// Self-transfer repair (2026-11-06): a row stored as money in/out that was a
// transfer from the wallet to itself. dry_run=true answers the measured effect
// and changes nothing (the confirm dialog's preview).
export const reclassifySelfTransfer       = (transactionId, { dryRun = false } = {}) =>
  post(`/blockchain-transactions/${transactionId}/reclassify-self-transfer`, { dry_run: !!dryRun });
export const getWalletAddresses           = ()              => get('/blockchain/wallet-addresses');
export const unlinkWalletAddress          = (id)            => del(`/blockchain/wallet-addresses/${id}`);
export const getLabelSuggestion = (customerId) => get(`/blockchain/address-labels/suggest?customer_id=${customerId}`);
export const updateAddressLabel = (id, data)   => put(`/blockchain/address-labels/${id}`, data);

// Resolve public (Tronscan) tags for a batch of addresses on demand (used by the
// add-address form). TRON-only; untagged addresses come back as null.
export const resolvePublicLabels = (items) => post('/blockchain/public-labels/resolve', { items });
// Load ALL locally-cached public tags once ({ "<address>": "Binance-Hot 6" }).
// The AddressLabel provider uses this so browsing never triggers a live lookup.
export const getPublicLabels    = ()      => get('/blockchain/public-labels');
// OFAC-sanctioned address list (loaded once; used to flag matches).
export const getSanctionedAddresses = ()  => get('/blockchain/sanctions');

// ============ Pending tasks / task tracking ============
export const getPendingTasks            = ()                       => get('/pending-tasks');
// updatePendingTaskStatus / completeTask removed (task-mirror audit
// 2026-08-13): task status is module-owned; the server now refuses bare
// status writes for every task type (TASK_STATUS_MODULE_OWNED).
export const claimTask                  = (taskId)                 => post(`/tasks/${taskId}/claim`);
export const releaseTask                = (taskId)                 => post(`/tasks/${taskId}/release`);
// My Tasks / Created by Me: with { page, per_page } they answer one server page
// { rows, total, page, per_page, pages } (the ONE paging contract —
// utils/paging.js); without, the legacy bare array.
export const getMyTasks                 = (status = 'all', params = {}) => get('/tasks/my-tasks', { params: { status, ...params } });
export const getTasksCreatedByMe        = (status = 'all', params = {}) => get('/tasks/created-by-me', { params: { status, ...params } });
// All Tasks: every task with its full progress trail, whoever holds it. PAGED
// (audit T7): resolves to { items, total, limit, has_more, next_cursor,
// focus_id, focus_found, focus_extra }. `cursor` is the previous page's
// next_cursor; `focusId` makes that task part of the answer even when it is
// beyond the page (a notification / "Open in Task Tracking" jump).
export const getAllTasks = (status = 'all', { limit = 100, cursor = null, focusId = null } = {}) => {
  const params = { status, limit };
  if (cursor) params.cursor = cursor;
  if (focusId != null) params.focus_id = focusId;
  return get('/tasks/all', { params });
};
export const getTaskStats               = ()                       => get('/tasks/stats');

// ============ Payment receipts (ORDER receiver accounts) ============
//
// ⚠ ORPHANED FROM THE SPA (2026-08-02). The only component that ever called
// this family — components/UploadReceiptModal.js — had zero importers and was
// deleted in the duplicate-surface batch. NOTHING in the app renders an order
// payment-receipt form today, so uploadPaymentReceipt / getPaymentStatus /
// getPaymentReceipts / revertPaymentReceipt / getReceiverBankAccounts /
// addReceiverBankAccounts / updateReceiverBankAccount / deleteReceiverBankAccount
// are all currently unreachable.
//
// They are KEPT ON PURPOSE, together with their server endpoints — see the full
// note above POST /api/orders/:orderId/payment-receipts in backend/server.js.
// Short version: order_payment_receipts is empty on both tenants today, but the
// table is still wired into the consumed-set and the order/bank-tx delete
// guards, so retiring the flow means retiring that machinery WITH it. That is
// an operator decision, not a cleanup. Re-wire a UI to these, or retire the
// whole flow deliberately — do not remove half of it.
//
// uploadPaymentReceipt has two paths: a multipart upload when receipt_image
// is a real File (most common), and a JSON fallback for URL-based receipts.
export const uploadPaymentReceipt = (orderId, receiptData) => {
  if (receiptData.receipt_image instanceof File) {
    const formData = new FormData();
    formData.append('receipt_image', receiptData.receipt_image);
    formData.append('receiver_bank_account_id', receiptData.receiver_bank_account_id);
    // Funding source: a real bank/cash account, or a customer account that
    // fronts the payment (backend settles it on the customer ledger).
    if (receiptData.source_type === 'customer') {
      formData.append('source_type', 'customer');
      formData.append('debited_customer_id', receiptData.debited_customer_id);
    } else {
      formData.append('debited_bank_account_id', receiptData.debited_bank_account_id);
    }
    if (receiptData.amount !== undefined && receiptData.amount !== null) {
      formData.append('amount', receiptData.amount);
    }
    return postForm(`/orders/${orderId}/payment-receipts`, formData);
  }
  return post(`/orders/${orderId}/payment-receipts`, receiptData);
};

// Upload a payment-request proof screenshot (the payee bank-account details the
// customer sent in). Returns { url }. Attach that url to each requested payee.
export const uploadPaymentProof = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return postForm('/upload/payment-proof', formData);
};

// ONE customer payment receipt for a P2P Sell order. A sell can carry SEVERAL
// ("1 receipt = 1 PR"), so each image is uploaded on its own and the resulting
// paths are posted together as receipt_urls — the same upload-first shape the
// multi-payment Request Payment form uses for its per-payment proofs.
export const uploadPlatformReceipt = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return postForm('/upload/platform-receipt', formData);
};

// ---- Customer-based Request Payment (sales create, finance fulfil) ----
// createPaymentRequest: draws on the customer's balance, pays to their bank
// account, with a compulsory proof screenshot url. No order involved.
export const createPaymentRequest = (data) => post('/payment-requests', data);
// submitPaymentReceived: sales uploads the receipt(s) the CUSTOMER handed
// them ("the customer paid us") + a note for finance. `data` is
// { receipts: [{ receipt_url, amount }, ...], note, customer_id?, amount? } —
// 1..20 rows, one row per slip ("1 receipt = 1 PR"); `amount` at the top
// level is an OPTIONAL cross-check the server 400s on if it doesn't match the
// sum of the rows. The legacy single-receipt shape
// { receipt_url, amount, note, customer_id? } is still accepted and behaves
// as exactly one row. Finance claims each slip from the pool and records the
// real bank credit via createTransaction's payment_received_task_id +
// payment_received_receipt_id (see RecordPaymentReceivedModal).
export const submitPaymentReceived = (data) => post('/tasks/payment-received', data);
// fulfillPaymentRequest: finance pays a request in one or more slips in a single
// submission. `slips` is an array of { receiptFile, source_type,
// debited_bank_account_id?, debited_customer_id?, amount }. Each slip carries its
// own receipt; the backend records them in ONE transaction → one snapshot, one
// grouped tx-history row. receipt_images[i] ↔ payments[i] by order.
export const fulfillPaymentRequest = (taskId, slips) => {
  const list = Array.isArray(slips) ? slips : [slips];
  const formData = new FormData();
  const payments = list.map((s) => {
    formData.append('receipt_images', s.receiptFile || s.receipt_image);
    return {
      amount: s.amount,
      source_type: s.source_type,
      debited_bank_account_id: s.source_type === 'customer' ? null : s.debited_bank_account_id,
      debited_customer_id: s.source_type === 'customer' ? s.debited_customer_id : null,
      description: s.description || '',
    };
  });
  formData.append('payments', JSON.stringify(payments));
  return postForm(`/payment-requests/${taskId}/fulfill`, formData);
};
// Undo one fulfilment tranche (money + ledgers reversed, task reopened).
// For a still-PREPARED tranche this cancels it with no money reversal.
export const revertPaymentRequestPayment = (taskId, paymentId) => post(`/payment-requests/${taskId}/payments/${paymentId}/revert`);
// completePaymentRequestTranche: finance confirms the bank actually debited a
// PREPARED tranche — the point the money moves + a checkpoint snapshot fires.
export const completePaymentRequestTranche = (taskId, paymentId) => post(`/payment-requests/${taskId}/payments/${paymentId}/complete`);
// Cancel an entirely-unpaid request (sales creator or finance).
export const cancelPaymentRequest = (taskId, reason = '') => del(`/payment-requests/${taskId}`, { data: { reason } });

export const getPaymentReceipts            = (orderId)                                   => get(`/orders/${orderId}/payment-receipts`);
export const getPaymentStatus              = (orderId, receiverAccountId)                => get(`/orders/${orderId}/receiver-accounts/${receiverAccountId}/payment-status`);
export const getReceiverBankAccounts       = (orderId)                                   => get(`/orders/${orderId}/receiver-bank-accounts`);
export const revertPaymentReceipt          = (orderId, receiptId)                        => post(`/orders/${orderId}/payment-receipts/${receiptId}/revert`);
export const addReceiverBankAccounts       = (orderId, bankAccounts)                     => post(`/orders/${orderId}/receiver-bank-accounts`, { bank_accounts: bankAccounts });
export const updateReceiverBankAccount     = (orderId, accountId, data)                  => put(`/orders/${orderId}/receiver-bank-accounts/${accountId}`, data);
export const deleteReceiverBankAccount     = (orderId, accountId)                        => del(`/orders/${orderId}/receiver-bank-accounts/${accountId}`);

// ============ Exchange rates ============
export const getExchangeRates = () => get('/exchange-rates');

// ============ Notifications ============
// Tabbed lifecycle (2026-08-13): unread | read | archived — archived is
// DERIVED server-side ("handled by others" / FYI already seen), never stamped.
// Returns { items, counts: { unread, read, archived } }.
export const getNotificationsTab            = (tab, limit = 30, offset = 0) => get(`/notifications?tab=${tab}&limit=${limit}&offset=${offset}`);
export const getNotificationById            = (notificationId)    => get(`/notifications/${notificationId}`);
export const getUnreadNotificationCount     = ()                  => get('/notifications/unread-count');
export const markNotificationAsRead         = (notificationId)    => put(`/notifications/${notificationId}/read`);
export const markAllNotificationsAsRead     = ()                  => put('/notifications/mark-all-read');
export const deleteNotification             = (notificationId)    => del(`/notifications/${notificationId}`);

// ============ Customer accounts (account-centric ledger) ============
export const getCustomerAccountBalances        = (customerId)                                => get(`/customers/${customerId}/account-balances`);
export const getCustomerAccountSummary         = (customerId)                                => get(`/customers/${customerId}/account-summary`);
export const getCustomerDetails                = (customerId)                                => get(`/customers/${customerId}/details`);
export const adjustCustomerAccount             = (customerId, adjustmentData)                => post(`/customers/${customerId}/account-adjustment`, adjustmentData);
export const setCustomerInitialBalance         = (customerId, balanceData)                   => post(`/customers/${customerId}/initial-balance`, balanceData);

// paged: pass { page, per_page } as the 5th argument → { success, data: <page>,
// total, page, per_page, pages } (running balances correct on every page);
// the positional limit/offset form is the legacy full-walk answer.
export const getCustomerAccountTransactions = (customerId, currencyType = null, limit = 50, offset = 0, paged = null) => {
  const params = paged ? { ...paged } : { limit, offset };
  if (currencyType) params.currency_type = currencyType;
  return get(`/customers/${customerId}/account-transactions`, { params });
};

// Mark or un-mark a blockchain transaction as a dust attack. Dust txs are
// hidden from default views, don't create pending tasks and don't mirror into
// crypto_transactions. Since 2026-10-14 dust is our own coins: a mark takes
// the transfer out of Unattributed (Net assets move by it, stated on the
// checkpoint); a manual mark above the automatic dust size is allowed (the
// page confirms it) and flagged in the response/checkpoint. Pass
// `mark: false` to restore a previously-marked-as-dust transaction.
export const setBlockchainTransactionDust = (transactionId, mark = true) =>
  post(`/blockchain-transactions/${transactionId}/dust`, { mark });

// Dedicated un-dust: restore a dust-flagged blockchain transaction to the
// normal list and back into Unattributed (balances already count it). Used by
// the shared CryptoTransactionTable's "Undust" action.
// Endpoint POST /api/blockchain-transactions/:id/undust is added server-side in
// parallel; :id is the blockchain_transactions row id.
export const undustBlockchainTransaction = (transactionId) =>
  post(`/blockchain-transactions/${transactionId}/undust`);

// Customer-to-customer direct ledger transfer ("飞账").
// Same currency on both sides. Writes paired debit/credit rows against
// customer_account_transactions without touching any bank account.
// `breakdown` (optional): array of { amount, description } sub-rows that should
// sum to `amount`. When passed, the backend writes ONE pair of debit/credit
// rows for the SUM and stashes the breakdown as JSON metadata so transaction
// history can render an expandable list. Use this for multi-line same
// sender/recipient/currency batch entries (e.g. one big settlement broken into
// 3 line-items the operator wants to remember separately).
// `receipt_url` (required): proof image URL for the transfer; the backend
// rejects the request without it.
export const createCustomerTransfer = ({ from_customer_id, to_customer_id, amount, currency_type, description, transferred_at, breakdown, receipt_url }) =>
  post('/customer-transfers', { from_customer_id, to_customer_id, amount, currency_type, description, transferred_at, breakdown, receipt_url });


// Reverse a customer-to-customer transfer. Accepts the shared reference_id
// from either ledger leg; the backend looks up the paired row, rolls back
// BOTH balances atomically, and deletes both rows. Admin/finance only.
export const deleteCustomerTransfer = (transferId) => del(`/customer-transfers/${transferId}`);

// Delete a single customer-ledger row. Backend decides per reference_type:
//   • manual_adjustment → direct reverse + delete
//   • money_transaction / crypto_transaction / blockchain_transaction →
//       unlink source tx from customer + reverse + delete
//   • initial_balance / order* / *_tx_delete / customer_transfer → rejected
//     with an explanation. customer_transfer has its own endpoint above
//     because both legs need to come down atomically.
export const deleteCustomerAccountTransaction = (ledgerRowId) => del(`/customer-account-transactions/${ledgerRowId}`);

// ============ Transaction assignment + history ============
// onBehalf: the Payment Received form's holiday-cover link (the sales already
// confirmed they act for another PIC's customer) — see requireCustomerActionAccessOrCover.
// receiptUrls: the customer's payment receipt SET — compulsory for sales when linking a credit finance already recorded (2026-10-29).
export const assignTransactionToCustomer            = (transactionId, customerId, { onBehalf = false, receiptUrls } = {}) => post(`/transactions/${transactionId}/assign-to-customer`, {
  customer_id: customerId,
  ...(onBehalf ? { on_behalf: true } : {}),
  ...(Array.isArray(receiptUrls) && receiptUrls.length ? { receipt_urls: receiptUrls } : {}),
});
export const assignCryptoTransactionToCustomer      = (transactionId, customerId) => post(`/crypto-transactions/${transactionId}/assign-to-customer`, { customer_id: customerId });
export const assignBlockchainTransactionToCustomer  = (transactionId, customerId) => post(`/blockchain-transactions/${transactionId}/assign-to-customer`, { customer_id: customerId });
// getTransactionHistory: with { page, per_page } → { rows, page, per_page, has_more }
// (server pages over the seven merged sources; no total). Page-only params:
// hide_dust=1, classification=unclassified|classified, focus_source+focus_id
// (one row for a Pending-Tasks deep-link). Without page → the legacy array.
export const getTransactionHistory                  = (params = {})               => get('/transaction-history', { params });
export const unlinkMoneyTransaction                 = (transactionId)             => post(`/transactions/${transactionId}/unlink`);
export const unlinkCryptoTransaction                = (transactionId)             => post(`/crypto-transactions/${transactionId}/unlink`);
export const unlinkBlockchainTransaction            = (transactionId)             => post(`/blockchain-transactions/${transactionId}/unlink`);

// getUnclaimedBankCredits: incoming bank credits finance has recorded that
// NOBODY has claimed yet. Two modes:
//   * no `amount` — the browse list behind the P2P Sell create form's picker
//     (all of them, newest first), the money-in twin of the normal order form's
//     unlinked-transaction list.
//   * `amount`    — the duplicate-PR fail-safe: "is there already an unclaimed
//     credit for exactly this figure?", matched to the cent.
// Minimal shape either way:
// { id, amount, description (the sender name finance typed), created_at,
//   reference_no, account_label }.
export const getUnclaimedBankCredits = (amount) =>
  get('/bank-credits/unclaimed', {
    params: (amount === undefined || amount === null || amount === '') ? {} : { amount },
  });

export const getUnassignedTransactions = (transactionType = null) => {
  const params = {};
  if (transactionType) params.transaction_type = transactionType;
  return get('/transactions/unassigned', { params });
};

export const getUnassignedCryptoTransactions = (cryptoType = null, transactionType = null) => {
  const params = {};
  if (cryptoType) params.crypto_type = cryptoType;
  if (transactionType) params.transaction_type = transactionType;
  return get('/crypto-transactions/unassigned', { params });
};

// ============ Internal-swap mark/revert ============
// `matchingTransactionId` triggers the LINK path on the backend (no new
// transaction inserted, no balance change) — use it when the operator
// has already entered the counterpart leg manually. If null, the legacy
// CREATE path runs (auto-inserts a mirror at the destination bank). The
// CREATE path inflates NAV by `amount` whenever the counterpart already
// exists separately — see snapshot-87 class of bug.
export const markMoneyTransactionAsSwap = (transactionId, toBankAccountId, description = null, matchingTransactionId = null) =>
  post(`/transactions/${transactionId}/mark-as-swap`, {
    to_bank_account_id: toBankAccountId,
    description,
    matching_transaction_id: matchingTransactionId,
  });

// (2026-05-14) `matchingTransactionSource` was added so the backend knows
// which table the matching id refers to. Step 2 of the swap flow returns
// rows from both crypto_transactions and blockchain_transactions; before
// this change the link request silently 404'd when the matching row was
// a blockchain row.
export const markCryptoTransactionAsSwap = (transactionId, toSubaccountId, description = null, matchingTransactionId = null, matchingTransactionSource = null) =>
  post(`/crypto-transactions/${transactionId}/mark-as-swap`, {
    to_subaccount_id: toSubaccountId,
    description,
    matching_transaction_id: matchingTransactionId,
    matching_transaction_source: matchingTransactionSource,
  });

export const markBlockchainTransactionAsSwap = (transactionId, toSubaccountId, description = null, matchingTransactionId = null, matchingTransactionSource = null) =>
  post(`/blockchain-transactions/${transactionId}/mark-as-swap`, {
    to_subaccount_id: toSubaccountId,
    description,
    matching_transaction_id: matchingTransactionId,
    matching_transaction_source: matchingTransactionSource,
  });

// Revert a swap - unbind two linked swap transactions. POST body optional:
// { blockchain_transaction_id } — required when the crypto leg has no
// related_transaction_id (crypto ↔ blockchain internal link).
export const revertCryptoSwap = (transactionId, body = {}) => post(`/crypto-transactions/${transactionId}/revert-swap`, body);
// Revert a mistaken platform CONVERSION from either entrance (2026-08-15):
// the leg in a wallet's tx list, or the order itself. Balances restored,
// legs removed, order marked cancelled; refused if the converted-in funds
// already left the wallet.
export const revertConvertLeg   = (transactionId) => post(`/crypto-transactions/${transactionId}/revert-convert`);
export const revertConvertOrder = (orderId)       => post(`/platform-orders/${orderId}/convert/revert`);
// Step-by-step unwind of a COMPLETED p2p trade (2026-08-15): the current
// platform handler undoes Done Trade (crypto leg reversed, order back to
// paid); finance then reverts payments; sales then cancels. Never bulk.
export const revertPlatformDone = (orderId)       => post(`/platform-orders/${orderId}/revert-done`);

// Revert linked bank swap (two statement rows wired via related_transaction_id).
export const revertMoneyInternalSwap = (transactionId) => post(`/transactions/${transactionId}/revert-swap`);

// Revert a blockchain↔blockchain internal-swap link. Use when the merged pair
// in Transaction History is two `blockchain_transactions` rows sharing the
// same tx_hash (no related_transaction_id available, only the [Internal Swap]
// description marker). Server fans out by tx_hash and clears every sibling
// blockchain row + crypto mirror.
export const revertBlockchainInternalSwap = (transactionId) => post(`/blockchain-transactions/${transactionId}/revert-swap`);

export const getSwapDestinations = (type) => get('/swap-destinations', { params: { type } });

export const getSwapMatchingTransactions = (subaccountId, { crypto_type, amount, direction, tolerance }) =>
  get(`/swap-matching-transactions/${subaccountId}`, { params: { crypto_type, amount, direction, tolerance } });

// Mirrors getSwapMatchingTransactions shape but for fiat — the backend
// returns rows with `transaction_source: 'money'`, `match_amount`, and
// `match_type` ('credit'|'debit') so the same Step-2 UI can render them.
export const getBankSwapMatchingTransactions = (bankAccountId, { amount, direction, tolerance }) =>
  get(`/swap-matching-transactions/bank/${bankAccountId}`, { params: { amount, direction, tolerance } });

// ============ Expenses ============
// The submit form posts multipart FormData (the invoice file rides along);
// a plain object still goes as JSON.
export const submitExpenseClaim       = (expenseData)                  =>
  (expenseData instanceof FormData) ? postForm('/expenses', expenseData) : post('/expenses', expenseData);
// getExpenses(): the legacy full array (every figure on the Expenses page sums
// these rows). getExpenses({ page, per_page, status, kind, category, focus_id })
// is the server-paged All-Time list → { rows, total, page, per_page, pages,
// focus_extra? } (backend/lib/paging.js); focus_id is returned as an extra FIRST
// row when it lies outside the page.
export const getExpenses              = (params = {})                  => get('/expenses', { params });
export const getExpenseSubmitters     = ()                             => get('/expenses/submitters');
// reviewExpense: admin approve/reject of a pending claim. A REJECTION must
// carry a reason — the server refuses without one and the submitter is told
// exactly what is written here.
export const reviewExpense            = (expenseId, action, notes)     => put(`/expenses/${expenseId}/review`, { action, notes });
// unapproveExpense: the approving admin sends an approved-but-unclaimed
// claim back to pending review.
export const unapproveExpense         = (expenseId)                    => post(`/expenses/${expenseId}/unapprove`);
// Customer-account entry (2026-08-13): a non-trading PROFIT or a DEPOSIT
// received INTO a customer's account — their balance moves toward owes-us
// (they hold the money for us). Finance only; undo reverses balance + rows.
export const recordExpenseCustomerEntry = (payload)                    => post('/expenses/customer-entry', payload);
export const undoExpenseCustomerEntry   = (expenseId)                  => post(`/expenses/${expenseId}/customer-entry/undo`);
// Customer balance WRITE-OFF ("Deactivate", operator 2026-10-14). Admin only.
// preview → the effect in words + any open work that blocks it; writeOff
// contras the balance to 0 and books the loss (they owed us) / profit (we owed
// them); undo = the same admin, while the account has not moved since.
export const previewCustomerWriteOff    = (customerId, currency)       => get(`/customers/${customerId}/write-off/preview`, { params: { currency } });
export const writeOffCustomerBalance    = (customerId, currency, expectedBalance) =>
  post(`/customers/${customerId}/write-off`, { currency, expected_balance: expectedBalance });
export const undoCustomerWriteOff       = (expenseId)                  => post(`/expenses/${expenseId}/write-off/undo`);
// Write-off limit (2026-10-24): one system-wide MYR threshold below which Deactivate is allowed.
export const getCustomerWriteOffLimit    = ()                           => get('/settings/customer-write-off-limit');
export const updateCustomerWriteOffLimit = (limitMyr)                   => put('/settings/customer-write-off-limit', { limit_myr: limitMyr });
export const getCustomerWriteOffLimitLog = ()                           => get('/settings/customer-write-off-limit/log');
// ---- PER-PAYMENT-DETAIL payout (2026-08-10) ----------------------------
// A claim is paid one PAYEE at a time. Claiming/releasing goes through the
// normal task pool (claimTask/releaseTask on detail.task_id) — these are the
// money steps for ONE detail.
//
// prepareExpenseDetailPayment: receipt + funding source, NO money moves. Send
// FormData with `payment_proof` plus paid_from_bank_id XOR paid_from_customer_id.
export const prepareExpenseDetailPayment = (expenseId, detailId, formData) =>
  postForm(`/expenses/${expenseId}/details/${detailId}/payment`, formData);
// PER-SLIP routes (2026-08-14): one payee may be settled by SEVERAL slips
// from mixed sources — each slip cancels/completes/undoes individually. Complete
// is THE money step (debits the chosen bank, or draws the customer balance, for
// that slip and snapshots); the claim flips to 'paid' only when the last
// detail completes. The older whole-payee prepare-undo / complete / complete-undo
// clients are gone — the per-slip three below replaced them.
export const cancelExpenseDetailSlip        = (expenseId, detailId, paymentId) => post(`/expenses/${expenseId}/details/${detailId}/payments/${paymentId}/undo`);
export const completeExpenseDetailSlip      = (expenseId, detailId, paymentId) => post(`/expenses/${expenseId}/details/${detailId}/payments/${paymentId}/complete`);
export const undoExpenseDetailSlipCompletion = (expenseId, detailId, paymentId) => post(`/expenses/${expenseId}/details/${detailId}/payments/${paymentId}/complete/undo`);

// RETIRED whole-expense money routes — the server answers 409
// EXPENSE_PER_DETAIL_FLOW. Kept only so an old import cannot break the build;
// nothing should call them. Use the per-detail routes above.
// claimExpense: finance claims an approved-but-unclaimed expense so they
// (and only they) can prepare its payment.
// (Phase 2, 2026-08-28: the six whole-expense claim/payment clients died with
// their 409-tombstoned routes — expenses are claimed and paid PER DETAIL via
// the generic task endpoints + the per-detail payment routes above.)
// amendExpense: the submitter edits their own still-pending request.
export const amendExpense             = (expenseId, data)             => put(`/expenses/${expenseId}`, data);
// Admin: rename title/description at any stage (metadata only, NAV-neutral)
export const updateExpenseLabels      = (expenseId, data)             => patch(`/expenses/${expenseId}/labels`, data);
export const markTransactionAsExpense = (data)                         => post('/expenses/from-transaction', data);
export const revertExpense            = (expenseId)                    => del(`/expenses/${expenseId}`);
// cancelExpense: the submitter cancels their own claim. NOTE the verb is PUT.
export const cancelExpense            = (expenseId)                    => put(`/expenses/${expenseId}/cancel`);

// ============ Platform module ============
export const getPlatformConfig         = ()                       => get('/platforms/config');
export const getPlatformAccounts       = ()                       => get('/platform-accounts');
export const getPlatformAccount        = (accountId)              => get(`/platform-accounts/${accountId}`);
export const createPlatformAccount     = (accountData)            => post('/platform-accounts', accountData);
export const claimPlatformAccount      = (accountId)              => post(`/platform-accounts/${accountId}/claim`);
export const releasePlatformAccount    = (accountId)              => post(`/platform-accounts/${accountId}/release`);
export const getPlatformWallets        = (accountId)              => get(`/platform-accounts/${accountId}/wallets`);
export const getPlatformAccountHandlerHistory = (accountId)       => get(`/platform-accounts/${accountId}/handler-history`);   // who handled it, newest first (2026-11-03)
// Paged when `page` / `per_page` are passed → { rows, total, page, per_page, pages, pending_count };
// without them the legacy array (newest 200). Filters: status (comma list), order_type
// (comma list), platform_account_id, submitted_by, sort=asc|desc (paged only).
export const getPlatformOrders         = (params = {})            => get('/platform-orders', { params });
export const getPlatformOrderPeople    = ()                        => get('/platform-orders/people');   // Submitted by / Handler filter options (2026-11-03)
export const getPlatformOrder          = (orderId)                => get(`/platform-orders/${orderId}`);
export const getPlatformOrderPayments  = (orderId)                => get(`/platform-orders/${orderId}/payments`);
export const deletePlatformOrderPayment = (orderId, paymentId)    => del(`/platform-orders/${orderId}/payments/${paymentId}`);
export const completePlatformOrderPayment = (orderId, paymentId)  => post(`/platform-orders/${orderId}/payments/${paymentId}/complete`);
// linkPlatformOrderReceivedCredit: SALES points their P2P Sell order at a credit
// finance has ALREADY recorded off the bank statement. Books nothing new — it
// claims that credit for the order and advances it exactly as finance's
// per-payment complete would, so no payment-received task is raised and finance
// is never asked to record the same money twice.
export const linkPlatformOrderReceivedCredit = (orderId, transactionId) =>
  post(`/platform-orders/${orderId}/link-received-credit`, { transaction_id: transactionId });
export const markPlatformOrderPaid     = (orderId)                => post(`/platform-orders/${orderId}/mark-paid`);
export const completePlatformOrder     = (orderId)                => post(`/platform-orders/${orderId}/done`);
export const abandonPlatformOrder      = (orderId, reason = '')   => post(`/platform-orders/${orderId}/abandon`, { reason });
export const cancelPlatformOrder       = (orderId)                => put(`/platform-orders/${orderId}/cancel`);
export const claimPlatformOrderTask    = (orderId, detailSeq = null) => post(`/platform-orders/${orderId}/claim-task`, detailSeq != null ? { detail_seq: detailSeq } : {});
// Release is PER PAYMENT, exactly like claim: a split buy's card/payment names its
// detail_seq (the server refuses a seq-less release of a split order and never
// drops a payment you didn't name); single-task orders pass null. T4, 2026-10-11.
export const releasePlatformOrderTask  = (orderId, detailSeq = null) => post(`/platform-orders/${orderId}/release-task`, detailSeq != null ? { detail_seq: detailSeq } : {});
// ON-BEHALF SUBMISSION APPROVAL (operator 2026-08-08). Any sales user may submit
// onto a platform account they do not handle; the submission then waits for the
// account's CURRENT handler to approve it before finance is asked for anything.
// Both endpoints are gated server-side on being that handler — these wrappers
// carry no authority of their own.
export const approvePlatformOrderSubmission = (orderId)           => post(`/platform-orders/${orderId}/approve-submission`);
export const rejectPlatformOrderSubmission  = (orderId, reason = '') =>
  post(`/platform-orders/${orderId}/reject-submission`, { reason });
// Handler pulls back an approved / directly-submitted order while it is still
// pending and no money has moved (2026-09-16).
export const revokePlatformOrder            = (orderId, reason = '') =>
  post(`/platform-orders/${orderId}/revoke`, { reason });

// Delete platform account (admin only). Pass { force: true } to bypass
// safety checks that block deletion when the linked crypto subaccounts
// still hold balances or have been involved in cross-group transfers.
export const deletePlatformAccount = (accountId, { force = false } = {}) =>
  del(`/platform-accounts/${accountId}${force ? '?force=true' : ''}`);

// Create platform order (with screenshot upload)
export const createPlatformOrder = (orderData) => {
  const formData = new FormData();
  Object.keys(orderData).forEach((key) => {
    if (orderData[key] !== null && orderData[key] !== undefined) {
      formData.append(key, orderData[key]);
    }
  });
  return postForm('/platform-orders', formData);
};

// Add a payment to a platform order (finance - multiple payments support)
export const addPlatformOrderPayment = (orderId, source, amount, receiptFile, description, detailSeq = null) => {
  const formData = new FormData();
  // source is { source_type, bank_account_id? , debited_customer_id? }; a bare
  // id is still accepted (treated as a bank source) for backward-compatibility.
  if (source && typeof source === 'object' && source.source_type === 'customer') {
    formData.append('source_type', 'customer');
    formData.append('debited_customer_id', source.debited_customer_id);
  } else {
    formData.append('bank_account_id', (source && typeof source === 'object') ? source.bank_account_id : source);
  }
  formData.append('amount', amount);
  // Split p2p_buy: which payment detail this tranche pays (server validates the claim).
  if (detailSeq != null) formData.append('detail_seq', detailSeq);
  // Money-IN slips may have no bank-in proof (optional there) — appending a
  // null File would send the literal string "null" as the field.
  if (receiptFile) formData.append('receipt', receiptFile);
  if (description) formData.append('description', description);
  return postForm(`/platform-orders/${orderId}/payments`, formData);
};

// Edit submitted_at (and optionally description) for a platform order.
// Used to backfill the actual on-platform event time for manually recorded
// orders such as 'convert'.
export const editPlatformOrderTime = (orderId, submittedAtIso, description) => {
  const body = { submitted_at: submittedAtIso };
  if (description !== undefined) body.description = description;
  return put(`/platform-orders/${orderId}/edit-time`, body);
};

// ============ Reports (finance + admin) ============
// from / to are Malaysia-day dates (YYYY-MM-DD), inclusive.
export const getReportSummary       = (params = {})    => get('/reports/summary', { params });
export const getSalesBreakdown      = (params = {})    => get('/reports/sales-breakdown', { params });
// Daily digest (2026-11-02): one row per Malaysia day — the summary cut by day.
// params: { from, to (≤ 92 days), coin? } — default this month to today, USDT.
export const getReportDaily         = (params = {})    => get('/reports/daily', { params });

// Monthly commission sheet — replaces the manual Excel. params: { month:
// 'YYYY-MM', user_id? }. Admin-only edits go through commission-config.
export const getCommissionReport    = (params = {})    => get('/reports/commission', { params });
// Sales role only — the signed-in person's own figures (the server never takes a user_id).
export const getMyCommission        = (params = {})    => get('/my-performance/commission', { params });
export const getMySales             = (params = {})    => get('/my-performance/sales', { params });
export const getCommissionConfig    = ()                => get('/reports/commission-config');
export const updateCommissionConfig = (data)            => put('/reports/commission-config', data);

// ============ Checkpoints ============
export const getCheckpoints         = (params = {})    => get('/checkpoints', { params });
export const getCheckpointDetail    = (id)             => get(`/checkpoints/${id}`);
export const getLatestCheckpoint    = ()               => get('/checkpoints/latest');
export const getRateRevisions       = ()               => get('/checkpoints/rate-revisions');
export const getLedgerOrphans        = ()               => get('/admin/ledger-orphans');
export const getKnownCurrencies     = ()               => get('/currencies/known');
export const deleteCheckpoint       = (id)             => del(`/checkpoints/${id}`);
export const getCheckpointRates     = ()               => get('/settings/checkpoint-rates');
export const updateCheckpointRates  = (rates)          => post('/settings/checkpoint-rates', rates);

// Live state — computed from source tables (bank_accounts, crypto_balances,
// customer_account_balances, plus transaction tables for "owe to unknown").
// Use this for the "current position" view at the top of Checkpoints; use
// getLatestCheckpoint only when you specifically want the most recent
// FROZEN snapshot (e.g. for delta diffing against history).
export const getCurrentFinancialState = () => get('/financial-state/current');

// Manually capture a checkpoint snapshot of current balances.
// rates: optional { USDT?, USDC?, FDUSD? } per-checkpoint rate override
// keyed in by the operator at capture time. Pass undefined / {} to use
// the global system_settings.checkpoint_rates.
export const createManualCheckpoint = (note = '', rates) => {
  const body = { note };
  if (rates && typeof rates === 'object') body.rates = rates;
  return post('/checkpoints/manual', body);
};

// Preview a rate-revision over a range of past checkpoints. Returns the
// per-checkpoint diff (old vs new MYR aggregates) so the operator can
// confirm the impact before committing.
export const previewRateRevision = ({ from_id, rates }) =>
  post('/checkpoints/preview-rate-revision', { from_id, rates });

// Commit a rate revision. Audited; updates checkpoints from `from_id` to
// MAX(id). `reason` becomes part of the audit log entry.
export const applyRateRevision = ({ from_id, rates, reason }) =>
  post('/checkpoints/apply-rate-revision', { from_id, rates, reason });

// ============ Server logs (administrator) ============
// Through this client like every other call, so an expired session reaches the
// 401 interceptor (the two log pages used to call raw fetch and bypass it).
export const getLogsBrowse   = (hours, limit = 300)   => get(`/logs/browse?hours=${hours}&limit=${limit}`);
export const getLogs         = (params)               => get(`/logs?${params}`); // params: URLSearchParams
export const getLogStats     = ()                     => get('/logs/stats');
export const clearLogs       = ()                     => del('/logs');
// Downloads resolve to a Blob; no timeout (a week of logs is large).
export const downloadLogs    = (days, format)         => get(`/logs/download?days=${days}&format=${format}`, { responseType: 'blob', timeout: 0 });
export const downloadLogFile = (filename)             => get(`/logs/download/${encodeURIComponent(filename)}`, { responseType: 'blob', timeout: 0 });

// ============ Web Push ============
export const getVapidPublicKey    = ()          => get('/push/vapid-public-key');
export const savePushSubscription = (payload)   => post('/push/subscribe', payload);
export const removePushSubscription = (endpoint) => post('/push/unsubscribe', { endpoint });
export const sendPushTest         = ()          => post('/push/test');

// ============ AI assistant ============
// User side (the chat bubble) and the administrator page. The chat itself
// streams over fetch, not axios: see utils/aiChatStream.js.
export const getAiStatus                    = ()            => get('/ai/status');
export const getAiConversations             = (page = 1)    => get('/ai/conversations', { params: { page } });
export const getAiConversationMessages      = (id, page = 1) => get(`/ai/conversations/${id}/messages`, { params: { page } });
export const deleteAiConversation           = (id)          => del(`/ai/conversations/${id}`);
export const getAiAdminSettings             = ()            => get('/ai/admin/settings');
export const setAiApiKey                    = (api_key, workspace_id) => put('/ai/admin/key', { api_key, workspace_id: workspace_id || null });
export const removeAiApiKey                 = ()            => del('/ai/admin/key');
export const testAiApiKey                   = ()            => post('/ai/admin/key/test');
export const getAiModels                    = ()            => get('/ai/admin/models');
export const refreshAiModels                = ()            => post('/ai/admin/models/refresh');
export const updateAiRole                   = (role, body)  => put(`/ai/admin/roles/${role}`, body);
export const updateAiInstructions           = (body)        => put('/ai/admin/instructions', body);
export const getAiUsage                     = (range)       => get('/ai/admin/usage', { params: { range } });
export const getAiAdminConversations        = (params)      => get('/ai/admin/conversations', { params });
export const getAiAdminConversationMessages = (id, page = 1) => get(`/ai/admin/conversations/${id}/messages`, { params: { page } });
export const getAiSettingsLog               = (page = 1)    => get('/ai/admin/settings-log', { params: { page } });

export default api;

// ============ AI staff (administrator only) ============
// External AI agents that record customer orders through a machine key.
// Writes sit behind the admin passkey step-up (the interceptor above runs the
// prompt and retries once); GETs are plain.
export const listAiAgents          = ()                => get('/admin/ai-agents');
export const createAiAgent         = (body)            => post('/admin/ai-agents', body);
export const updateAiAgent         = (id, body)        => put(`/admin/ai-agents/${id}`, body);
export const rotateAiAgentKey      = (id)              => post(`/admin/ai-agents/${id}/rotate-key`, {});
export const getAiAgentLog         = (id, params)      => get(`/admin/ai-agents/${id}/log`, { params });
export const listCustomerContacts  = (params)          => get('/admin/customer-contacts', { params });
export const addCustomerContact    = (body)            => post('/admin/customer-contacts', body);
export const deleteCustomerContact = (id)              => del(`/admin/customer-contacts/${id}`);
